import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import RequireFeature from "./components/RequireFeature";
import RequirePermission from "./components/RequirePermission";
import MainLayout from "./layouts/MainLayout";
import AppointmentDetail from "./pages/AppointmentDetail";
import BranchDetail from "./pages/BranchDetail";
import BranchProducts from "./pages/BranchProducts";
import BranchServices from "./pages/BranchServices";
import Brands from "./pages/Brands";
import Branches from "./pages/Branches";
import ClientProfile from "./pages/ClientProfile";
import Clients from "./pages/Clients";
import BusinessProfile from "./pages/BusinessProfile";
import CreateBranch from "./pages/CreateBranch";
import Dashboard from "./pages/Dashboard";
import Diary from "./pages/Diary";
import Login from "./pages/Login";
import LoyaltyMember from "./pages/LoyaltyMember";
import LoyaltyMembers from "./pages/LoyaltyMembers";
import LoyaltyProgram from "./pages/LoyaltyProgram";
import LoyaltyPrograms from "./pages/LoyaltyPrograms";
import LoyaltyRedeem from "./pages/LoyaltyRedeem";
import LoyaltyRewards from "./pages/LoyaltyRewards";
import Pricing from "./pages/Pricing";
import Register from "./pages/Register";
import NotificationTarget from "./pages/NotificationTarget";
import ProductCategories from "./pages/ProductCategories";
import Products from "./pages/Products";
import ProductForm from "./pages/ProductForm";
import Reports from "./pages/Reports";
import Reviews from "./pages/Reviews";
import Alerts from "./pages/Alerts";
import ProviderProfile from "./pages/ProviderProfile";
import Providers from "./pages/Providers";
import Roles from "./pages/Roles";
import ServiceCatalogue from "./pages/ServiceCatalogue";
import ServiceCategories from "./pages/ServiceCategories";
import Staff from "./pages/Staff";
import Transactions from "./pages/Transactions";
import Security from "./pages/Security";
import { adoptUserLanguage } from "./i18n";
import { fetchMe, getCurrentUser, isAuthenticated, logout } from "./services/authService";

/** Signed out: to login, remembering where they were going. */
function ToLogin() {
  const location = useLocation();
  return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
}

/** Just signed in on /login: continue to where they were going, else home. */
function AfterLogin() {
  const from = useLocation().state?.from;
  return <Navigate to={typeof from === "string" && from.startsWith("/") ? from : "/"} replace />;
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [user, setUser] = useState(getCurrentUser());

  // Keep tabs in sync when one of them logs in or out.
  useEffect(() => {
    const sync = () => {
      setAuthenticated(isAuthenticated());
      setUser(getCurrentUser());
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  // The cached profile is what every `can()` in the portal reads, and it was
  // only ever written at login — so a permission granted or withdrawn while
  // somebody was signed in did not reach their screen until they logged out
  // and back in. Refetch it whenever the app boots and whenever the tab is
  // brought back to the front, and render the cached copy meanwhile so nothing
  // flashes. Hiding a screen is only ever a convenience; the server refuses
  // the action either way.
  useEffect(() => {
    if (!authenticated) return undefined;

    const refresh = ({ adoptLanguage = false } = {}) => {
      fetchMe()
        .then((fresh) => {
          setUser(fresh);
          // On load only: a reload keeps the user's own language. Not on every
          // tab focus, which would undo a switch made while it was in flight.
          if (adoptLanguage) adoptUserLanguage(fresh);
        })
        .catch(() => {});
    };

    refresh({ adoptLanguage: true });

    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [authenticated]);

  const handleLogin = useCallback((loggedInUser) => {
    setUser(loggedInUser);
    setAuthenticated(true);
  }, []);

  const handleLogout = useCallback(async () => {
    // Takes this browser's push token with it (see authService.logout).
    await logout();
    setUser(null);
    setAuthenticated(false);
  }, []);

  if (!authenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        {/* Public: the pricing page and self sign-up. Signed in, both fall
            through to the dashboard. */}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/register" element={<Register onLogin={handleLogin} />} />
        <Route path="*" element={<ToLogin />} />
      </Routes>
    );
  }

  return (
    <MainLayout user={user} onLogout={handleLogout}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/business" element={<BusinessProfile />} />
        {/* The diary and one booking's detail — BIZ-12 and BIZ-14. */}
        <Route
          path="/diary"
          element={<RequirePermission path="/diary"><Diary /></RequirePermission>}
        />
        <Route
          path="/appointments/:id"
          element={<RequirePermission path="/appointments/:id"><AppointmentDetail /></RequirePermission>}
        />
        <Route
          path="/branches"
          element={<RequirePermission path="/branches"><Branches /></RequirePermission>}
        />
        <Route
          path="/branches/create"
          element={<RequirePermission path="/branches/create"><CreateBranch /></RequirePermission>}
        />
        <Route
          path="/branches/:id"
          element={<RequirePermission path="/branches/:id"><BranchDetail /></RequirePermission>}
        />
        <Route
          path="/branches/:id/services"
          element={<RequirePermission path="/branches/:id/services"><BranchServices /></RequirePermission>}
        />
        <Route
          path="/branches/:id/products"
          element={<RequirePermission path="/branches/:id/products"><BranchProducts /></RequirePermission>}
        />
        {/* The business's own CRM — BIZ-15 and BIZ-16. */}
        {/* Reports v2 (spec 02) — /reports/:category/:reportKey; old ?tab= URLs redirect inside. */}
        {["/reports", "/reports/:category", "/reports/:category/:reportKey"].map((path) => (
          <Route
            key={path}
            path={path}
            element={<RequirePermission path="/reports"><Reports /></RequirePermission>}
          />
        ))}
        {/* Loyalty — permission per screen, and the plan's loyalty feature. */}
        {[
          ["/loyalty/programs", <LoyaltyPrograms />],
          ["/loyalty/programs/new", <LoyaltyProgram mode="new" />],
          ["/loyalty/programs/:programId", <LoyaltyProgram mode="view" />],
          ["/loyalty/programs/:programId/edit", <LoyaltyProgram mode="edit" />],
          ["/loyalty/rewards", <LoyaltyRewards />],
          ["/loyalty/members", <LoyaltyMembers />],
          ["/loyalty/members/:clientId", <LoyaltyMember />],
          ["/loyalty/redeem", <LoyaltyRedeem />],
        ].map(([path, page]) => (
          <Route
            key={path}
            path={path}
            element={
              <RequirePermission path={path}>
                <RequireFeature path={path}>{page}</RequireFeature>
              </RequirePermission>
            }
          />
        ))}
        {/* The single business-wide programme became one per branch. */}
        <Route path="/loyalty/program" element={<Navigate to="/loyalty/programs" replace />} />
        {/* Customer reviews per branch, read-only. */}
        <Route
          path="/reviews"
          element={<RequirePermission path="/reviews"><Reviews /></RequirePermission>}
        />
        <Route
          path="/alerts"
          element={<RequirePermission path="/alerts"><Alerts /></RequirePermission>}
        />
        {/* BIZ-19 — every transaction, with the money broken out. */}
        <Route
          path="/transactions"
          element={<RequirePermission path="/transactions"><Transactions /></RequirePermission>}
        />
        <Route
          path="/clients"
          element={<RequirePermission path="/clients"><Clients /></RequirePermission>}
        />
        <Route
          path="/clients/:id"
          element={<RequirePermission path="/clients/:id"><ClientProfile /></RequirePermission>}
        />
        <Route
          path="/services"
          element={<RequirePermission path="/services"><ServiceCatalogue /></RequirePermission>}
        />
        <Route
          path="/service-categories"
          element={<RequirePermission path="/service-categories"><ServiceCategories /></RequirePermission>}
        />
        <Route
          path="/product-categories"
          element={<RequirePermission path="/product-categories"><ProductCategories /></RequirePermission>}
        />
        {/* The product catalogue, like /services: defined once, assigned to branches explicitly. */}
        <Route
          path="/products"
          element={<RequirePermission path="/products"><Products /></RequirePermission>}
        />
        <Route
          path="/products/create"
          element={<RequirePermission path="/products/create"><ProductForm /></RequirePermission>}
        />
        <Route
          path="/products/:productId/edit"
          element={<RequirePermission path="/products/:productId/edit"><ProductForm /></RequirePermission>}
        />
        <Route
          path="/brands"
          element={<RequirePermission path="/brands"><Brands /></RequirePermission>}
        />
        <Route
          path="/staff"
          element={<RequirePermission path="/staff"><Staff /></RequirePermission>}
        />
        {/* Service providers — the people customers book, as opposed to the
            portal logins on /staff. */}
        <Route
          path="/providers"
          element={<RequirePermission path="/providers"><Providers /></RequirePermission>}
        />
        <Route
          path="/providers/:id"
          element={<RequirePermission path="/providers/:id"><ProviderProfile /></RequirePermission>}
        />
        <Route
          path="/roles"
          element={<RequirePermission path="/roles"><Roles /></RequirePermission>}
        />
        <Route path="/security" element={<Security user={user} />} />
        <Route path="/notification-target" element={<NotificationTarget />} />
        <Route path="/login" element={<AfterLogin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </MainLayout>
  );
}
